import type { PendingShopifyIntegrationCookie } from "schemas/cookies";

import * as React from "react";

import usePendingShopifyIntegrationCookie from "@v2/hooks/usePendingShopifyIntegrationCookie";
import { projectRouter, projectUtils } from "@v2/trpc/routes/project";
import { workspaceRouter, workspaceUtils } from "@v2/trpc/routes/workspace";
import { routes } from "@v2/utils/router";
import { generatePath, useNavigate } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

/**
 * Types of connection targets we may need to process in order to possibly
 * auto-connect an existing integration or a pending app installation to a
 * project or workspace.
 *
 * This target is determined by the combination of cookies which were set when
 * the user clicked an "install shopify" button in our app, the users
 * workspace/project memberships, etc.
 *
 * - Having no auto-connect target means that the user came in from Shopify
 *   without any specific intention that our app knows about.
 * - Having a specific cookie means they came from a specific place in our app
 *   which set that cookie.
 * - Having the "automatic" target means that they didn't come from a specific
 *   place in our app, but we've determined that if they just installed Shopify,
 *   we should auto-connect the store that they came from to a project/workspace
 *   anyway. This is useful in the case where the user installed a shopify store
 *   first, before ever making an account, and we want to connect it to their
 *   first and only project after they finish the onboarding flow.
 * - Having the "wait" autoconnect target means that we should wait until there's
 *   a project/workspace and try again later (for example in onboarding, before the
 *   user has created their first workspace and project)
 */
type PendingIntegrationAutoConnectTarget =
  | PendingShopifyIntegrationCookie
  | {
      type: "automatic";
      projectId: string;
      workspaceId?: string;
      hostingType?: "shopify" | "replo";
    }
  | { type: "wait" };

function getTargetPathBasedOnCookieType(
  pendingIntegrationAutoConnectTarget: PendingIntegrationAutoConnectTarget | null,
) {
  if (!pendingIntegrationAutoConnectTarget) {
    return null;
  }

  return exhaustiveSwitch(pendingIntegrationAutoConnectTarget)({
    automatic: ({ projectId, hostingType }) => {
      return hostingType === "replo"
        ? generatePath(routes.shops.home, { projectId })
        : `/editor/${projectId}/new`;
    },
    home: ({ projectId }) => {
      return generatePath(routes.shops.root, { projectId });
    },
    analytics: ({ projectId }) => {
      return generatePath(routes.shops.analytics.home, { projectId });
    },
    onboarding: ({ projectId, hostingType }) => {
      if (!projectId) {
        return generatePath(routes.shops.root);
      }
      return hostingType === "replo"
        ? generatePath(routes.shops.root, { projectId })
        : `/editor/${projectId}/new`;
    },
    noShopifyErrorModal: ({ projectId }) => {
      return generatePath(routes.editor.project, { projectId });
    },
    projectSettings: ({ projectId }) => {
      return generatePath(routes.editor.integrations, { projectId });
    },
    newProject: ({ projectId }) => {
      return generatePath(routes.editor.project, { projectId });
    },
    projectView: ({ projectId }) => {
      return generatePath(routes.editor.project, { projectId });
    },
    sections: ({ projectId }) => {
      return generatePath(routes.editor.project, { projectId });
    },
    publish: ({ projectId }) => {
      return generatePath(routes.editor.project, { projectId });
    },
    shopStyles: ({ projectId }) => {
      return generatePath(routes.editor.project, { projectId });
    },
    assets: ({ projectId }) => {
      return generatePath(routes.editor.project, { projectId });
    },
    productPicker: ({ projectId }) => {
      return generatePath(routes.shops.products.list, { projectId });
    },
    shopSettingsIntegration: ({ projectId }) => {
      return generatePath(routes.shops.settings.shops.integrations.home, {
        projectId,
      });
    },
    billingModal: ({ workspaceId }) => {
      return generatePath(routes.workspace.billing, { workspaceId });
    },
    preview: ({ projectId }) => {
      return generatePath(routes.editor.project, { projectId });
    },
    abtesting: ({ projectId, experimentId }) => {
      return generatePath(routes.shops.experiments.details, {
        projectId,
        experimentId,
      });
    },
    wait: () => null,
  });
}

/**
 * NOTE (Matt 2024-09-26): This hook is responsible for navigating a user coming
 * in from Shopify. There are several scenarios that this hooks needs to manage,
 * primarily those related to the shopify integration installation process but
 * including the use-case of a user simply clicking on Replo from within Shopify
 * after it has already been installed.
 *
 * The backend sends a user to `/home` with either a `pendingAppInstallationId`
 * or a `shopifyIntegrationId`, indicating whether it is a fresh install or if
 * the Shopify Store has already been connected to a workspace. Additionally,
 * the user may have `pendingShopifyIntegrationCookie` set, which would indicate
 * if the user had started the shopify install flow within the last 10 min from
 * within Replo. That cookie can indicate a workspace (and project, where
 * applicable) that the shopify store needs to be connected to.
 *
 * Take a look at the notes peppered throughout the useEffect to get a sense of
 * how we handle each case in this flow.
 */
export default function useConnectShopifyIntegrationNavigator() {
  const navigate = useNavigate();
  const [
    shouldShowPendingIntegrationModal,
    setShouldShowPendingIntegrationModal,
  ] = React.useState(false);

  const { data: userWorkspaces, isFetching: isUserWorkspacesDataLoading } =
    workspaceRouter.getUserWorkspacesList.useQuery();
  const { data: userProjects, isFetching: isUserProjectsLoading } =
    projectRouter.listWithStats.useQuery({});

  const { pendingShopifyIntegrationCookie } =
    usePendingShopifyIntegrationCookie();

  // NOTE (Matt 2024-09-30): If there is a pendingShopifyIntegrationCookie, then that is the target workspace
  // and/or project that we auto-connect an integration to. Alternatively, if the user only has access to 1
  // workspace and 1 project, then we autoconnect to that workspace and that project. This would only happen
  // if a user is coming from onboarding and they have not been invited to other projects or workspaces.
  const pendingIntegrationAutoConnectTarget: PendingIntegrationAutoConnectTarget | null =
    React.useMemo(() => {
      if (
        // Note (Noah, 2025-02-25): If we have a pendingShopifyIntegrationCookie,
        // then just return it HOWEVER if the cookie is for onboarding we want to
        // treat it as type automatic
        pendingShopifyIntegrationCookie
      ) {
        return pendingShopifyIntegrationCookie;
      }

      const projects = userProjects?.projects ?? [];
      if (!projects[0] || !userWorkspaces?.workspaces[0]) {
        return { type: "wait" };
      }

      const userHasOnlyOneWorkspace = userWorkspaces.workspaces.length === 1;
      const userHasOnlyOneProject = projects.length === 1;
      const usersOnlyProjectIsOwnedByUsersOnlyWorkspace =
        userHasOnlyOneWorkspace &&
        userHasOnlyOneProject &&
        projects[0].ownerWorkspace?.id === userWorkspaces.workspaces[0].id;
      if (usersOnlyProjectIsOwnedByUsersOnlyWorkspace) {
        return {
          type: "automatic",
          projectId: projects[0].id,
          workspaceId: userWorkspaces.workspaces[0].id,
          hostingType: projects[0].hostingType,
        };
      }
      return null;
    }, [pendingShopifyIntegrationCookie, userProjects, userWorkspaces]);

  const targetPath = getTargetPathBasedOnCookieType(
    pendingIntegrationAutoConnectTarget,
  );

  const {
    mutate: connectProjectToPendingInstallation,
    isPending: isConnectingProjectToPendingInstallation,
    isError: isProjectPendingConnectionError,
  } = projectRouter.connectToPendingInstallation.useMutation({
    onSuccess: () => {
      void projectUtils.listWithStats.invalidate();
      void workspaceUtils.getUserWorkspacesList.invalidate();
      navigate(targetPath!);
    },
  });
  const {
    mutate: connectProjectToExistingIntegration,
    isPending: isConnectingProjectToIntegration,
    isError: isProjectIntegrationConnectionError,
  } = projectRouter.connectToIntegration.useMutation({
    onSuccess: () => {
      void projectUtils.listWithStats.invalidate();
      void workspaceUtils.getUserWorkspacesList.invalidate();
      navigate(targetPath!);
    },
  });

  const isMutationLoading =
    isConnectingProjectToPendingInstallation ||
    isConnectingProjectToIntegration;
  const isMutationError =
    isProjectPendingConnectionError || isProjectIntegrationConnectionError;
  const isQueryLoading = isUserWorkspacesDataLoading || isUserProjectsLoading;

  React.useEffect(() => {
    // Note (Berkley , 2025-10-10): Read directly from window.location.search to avoid stale searchParams
    // The useSearchParams hook can be stale in parent components when child components navigate
    const searchParams = new URLSearchParams(window.location.search);
    const pendingAppInstallationId = searchParams.get(
      "pendingAppInstallationId",
    );
    const shopifyIntegrationId = searchParams.get("shopifyIntegrationId");

    // Note (Noah, 2025-02-25): If we're still loading then we might not have determined what to do in the
    // automatic post-onboarding case. Just return because we'll re-run this effect once we have the data
    if (isMutationLoading || isMutationError || isQueryLoading) {
      return;
    }

    // Note (Noah, 2025-02-25): If we have no pending app installation id AND no shopify integration id
    // in the params, then we didn't just come from Shopify. Just do nothing.
    if (!pendingAppInstallationId && !shopifyIntegrationId) {
      return;
    }

    // Note (Noah, 2025-02-25): Now we know that we came from Shopify, because we have a query param
    // indicating it.
    // Note (Matt 2024-09-26): If there is a pendingAppInstallationId, and a pendingIntegrationAutoConnectTarget
    // we should automatically connect the corresponding project or workspace to the pending install.
    if (pendingAppInstallationId) {
      if (!pendingIntegrationAutoConnectTarget) {
        // NOTE (Matt 2024-09-26): If there is a pendingAppInstallationId and no cookie,
        // we open the pendingShopifyIntegrationModal which prompts the user to connect
        // a workspace.
        setShouldShowPendingIntegrationModal(true);
        return;
      }

      // Note (Noah, 2025-02-25): Otherwise, based on where we came from, figure out the correct project
      // and/or workspace to connect the pending app installation to.
      exhaustiveSwitch(pendingIntegrationAutoConnectTarget)({
        automatic: ({ projectId }) => {
          connectProjectToPendingInstallation({
            projectId,
            shopify: { pendingAppInstallationId },
          });
        },
        home: ({ projectId }) => {
          connectProjectToPendingInstallation({
            projectId,
            shopify: { pendingAppInstallationId },
          });
        },
        wait: () => {
          return;
        },
        sections: ({ projectId }) => {
          connectProjectToPendingInstallation({
            projectId,
            shopify: { pendingAppInstallationId },
          });
        },
        projectView: ({ projectId }) => {
          connectProjectToPendingInstallation({
            projectId,
            shopify: { pendingAppInstallationId },
          });
        },
        noShopifyErrorModal: ({ projectId }) => {
          connectProjectToPendingInstallation({
            projectId,
            shopify: { pendingAppInstallationId },
          });
        },
        projectSettings: ({ projectId }) => {
          connectProjectToPendingInstallation({
            projectId,
            shopify: { pendingAppInstallationId },
          });
        },
        newProject: ({ projectId }) => {
          connectProjectToPendingInstallation({
            projectId,
            shopify: { pendingAppInstallationId },
          });
        },
        publish: ({ projectId }) => {
          connectProjectToPendingInstallation({
            projectId,
            shopify: { pendingAppInstallationId },
          });
        },
        shopStyles: ({ projectId }) => {
          connectProjectToPendingInstallation({
            projectId,
            shopify: { pendingAppInstallationId },
          });
        },
        preview: ({ projectId }) => {
          connectProjectToPendingInstallation({
            projectId,
            shopify: { pendingAppInstallationId },
          });
        },
        assets: ({ projectId }) => {
          connectProjectToPendingInstallation({
            projectId,
            shopify: { pendingAppInstallationId },
          });
        },
        productPicker: ({ projectId }) => {
          connectProjectToPendingInstallation({
            projectId,
            shopify: { pendingAppInstallationId },
          });
        },
        shopSettingsIntegration: ({ projectId }) => {
          connectProjectToPendingInstallation({
            projectId,
            shopify: { pendingAppInstallationId },
          });
        },
        billingModal: ({ projectId }) => {
          connectProjectToPendingInstallation({
            projectId,
            shopify: { pendingAppInstallationId },
          });
        },
        analytics: ({ projectId }) => {
          connectProjectToPendingInstallation({
            projectId,
            shopify: { pendingAppInstallationId },
          });
        },
        abtesting: ({ projectId }) => {
          connectProjectToPendingInstallation({
            projectId,
            shopify: { pendingAppInstallationId },
          });
        },
        onboarding: ({ projectId }) => {
          if (projectId) {
            connectProjectToPendingInstallation({
              projectId,
              shopify: { pendingAppInstallationId },
            });
          }
        },
      });
      searchParams.delete("pendingAppInstallationId");
      return;
    } else if (shopifyIntegrationId) {
      if (!pendingIntegrationAutoConnectTarget) {
        // NOTE (Matt 2024-09-26): If the user does not have a cookie, then they are just trying to access Replo
        // via Shopify, WITHOUT having triggered the navigation to Shopify through some place in Replo. We should
        // not even get here, because in this case we should have redirected them on the server to the relevant
        // project/workspace. So, just return null here.
        return;
      }

      // Note (Noah, 2025-02-25): We assume that we came from a workspace which
      // IS the same as the one which already owns the integration, so the only
      // thing to do is connect the integration to a specific project, if we
      // came to a specific project. Otherwise, redirect back to where we came
      // from.
      const projectId = exhaustiveSwitch(pendingIntegrationAutoConnectTarget)({
        automatic: ({ projectId }) => projectId,
        home: ({ projectId }) => projectId,
        sections: ({ projectId }) => projectId,
        noShopifyErrorModal: ({ projectId }) => projectId,
        projectSettings: ({ projectId }) => projectId,
        newProject: ({ projectId }) => projectId,
        publish: ({ projectId }) => projectId,
        shopStyles: ({ projectId }) => projectId,
        preview: ({ projectId }) => projectId,
        assets: ({ projectId }) => projectId,
        productPicker: ({ projectId }) => projectId,
        billingModal: ({ projectId }) => projectId,
        shopSettingsIntegration: () => null,
        projectView: ({ projectId }) => projectId,
        wait: () => null,
        analytics: ({ projectId }) => projectId,
        abtesting: ({ projectId }) => projectId,
        onboarding: ({ projectId }) => projectId,
      });
      if (projectId) {
        connectProjectToExistingIntegration({
          projectId,
          integrationId: shopifyIntegrationId,
        });
      } else {
        // Navigate to target path
        navigate(targetPath!);
      }
      searchParams.delete("shopifyIntegrationId");
    } else {
      // Note (Noah, 2025-02-25): This case will never be reached!
    }
  }, [
    isMutationLoading,
    isMutationError,
    isQueryLoading,
    pendingIntegrationAutoConnectTarget,
    connectProjectToPendingInstallation,
    connectProjectToExistingIntegration,
    navigate,
    targetPath,
  ]);

  return {
    isLoading: isMutationLoading || isQueryLoading,
    isError: isMutationError,
    shouldShowPendingIntegrationModal,
    setShouldShowPendingIntegrationModal,
  };
}
